import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { openSettings, QUICKMENU_KEY } from '../../SettingsPanel';
import { zoomIn, zoomOut } from '../../../utils/zoom';
import { ZoomInIcon, ZoomOutIcon } from '../../ZoomIcons';

/**
 * Mobile-only navigation (hybrid of options B + C, chosen 2026-07).
 *
 *  - Burger button floats top-right beside the domain name; opens a
 *    slide-in drawer with every menu item in labelled sections.
 *  - Below the search bar, the desktop pill rows render as horizontally
 *    swipeable strips ("quick menu"). A small control lets the visitor
 *    hide them; the choice persists in localStorage so returning
 *    visitors who hid it get the clean burger-only layout with no
 *    expander shown at all. It can be restored from the drawer.
 *
 * CSS lives in responsive.css; everything here is hidden above 991px and
 * the desktop nav is hidden below it, so exactly one nav renders per width.
 *
 * Sections mirror the data rows the desktop nav uses:
 *   Menu_Main  DT_Level1 '01' -> "Main menu"   (strip 1)
 *   Menu_Main  DT_Level1 '02' -> "Topics"      (strip 2)
 *   Menu_Main2                -> "Village pages" (strip 3, grey)
 *   Menu_Top                  -> "About"       (drawer only)
 */

const HIDE_KEY = QUICKMENU_KEY;

const itemLabel = (item) => (item.DT_Setting === 'Index' ? 'XHome' : item.DT_Setting);

const MobileMenu = ({ topItems, mainItems, villageItems }) => {
  // Dev aids (harmless in production):
  //   ?_menu=open        renders the drawer open on first paint
  //   ?_quickmenu=hidden renders the pills-hidden state
  const [open, setOpen] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('_menu') === 'open';
    } catch (e) {
      return false;
    }
  });
  const [pillsHidden, setPillsHidden] = useState(() => {
    try {
      if (new URLSearchParams(window.location.search).get('_quickmenu') === 'hidden') return true;
      return localStorage.getItem(HIDE_KEY) === '1';
    } catch (e) {
      return false;
    }
  });
  const closeBtnRef = useRef(null);
  const burgerRef = useRef(null);
  const location = useLocation();
  const currentPage = location.pathname.substring(1);
  const isIndexPage = location.pathname === '/';

  const hidePills = () => {
    setPillsHidden(true);
    try { localStorage.setItem(HIDE_KEY, '1'); } catch (e) { /* private mode */ }
  };

  // Stay in sync with the settings panel's "Show quick menu" toggle
  useEffect(() => {
    const onSettingsChanged = () => {
      try { setPillsHidden(localStorage.getItem(HIDE_KEY) === '1'); } catch (e) { /* ignore */ }
    };
    window.addEventListener('aw-settings-changed', onSettingsChanged);
    return () => window.removeEventListener('aw-settings-changed', onSettingsChanged);
  }, []);

  // Close when the route changes (user tapped a link). Skipped on first
  // render so the ?_menu=open dev aid isn't immediately undone.
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setOpen(false);
  }, [location.pathname]);

  // While open: lock body scroll, close on Escape, move focus into the panel
  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    if (closeBtnRef.current) closeBtnRef.current.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
      if (burgerRef.current) burgerRef.current.focus();
    };
  }, [open]);

  // Split Menu_Main into its two rows the same way mainMenu/index.js does
  const rows = (mainItems || []).reduce((acc, item) => {
    const row = item.DT_Level1 === '02' ? 1 : 0;
    if (!acc[row]) acc[row] = [];
    acc[row].push(item);
    return acc;
  }, []);

  const isCurrent = (item) =>
    currentPage === item.DT_Setting_URL ||
    (item.DT_Setting_URL === 'index' && isIndexPage);

  const renderPills = (items) =>
    (items || []).map((item, i) => (
      <Link
        key={i}
        to={'/' + item.DT_Setting_URL}
        className={'aw-strip-pill' + (isCurrent(item) ? ' current' : '')}
      >
        {itemLabel(item)}
      </Link>
    ));

  const renderDrawerLinks = (items) =>
    (items || []).map((item, i) => (
      <React.Fragment key={i}>
        <Link
          to={'/' + item.DT_Setting_URL}
          className={'aw-drawer-link' + (isCurrent(item) ? ' current' : '')}
        >
          {itemLabel(item)}
        </Link>
        {item.items && item.items.length > 0 &&
          item.items.map((sub, j) => (
            <Link key={'s' + j} to={'/' + sub.DT_Setting_URL} className="aw-drawer-link sub">
              {sub.DT_Setting}
            </Link>
          ))}
      </React.Fragment>
    ));

  const sections = [
    { label: 'Main menu', items: rows[0] },
    { label: 'Topics', items: rows[1] },
    { label: 'Village pages', items: villageItems },
    { label: 'About', items: topItems },
  ].filter((s) => s.items && s.items.length > 0);

  if (sections.length === 0) return null;

  const strips = [
    { items: rows[0], cls: '' },
    { items: rows[1], cls: '' },
    { items: villageItems, cls: ' village' },
  ].filter((s) => s.items && s.items.length > 0);

  return (
    <>
      <button
        ref={burgerRef}
        type="button"
        className="aw-burger-fab"
        aria-label="Open menu"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
          <path d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {!pillsHidden && (
        <div className="aw-quickmenu">
          {strips.map((s, i) => (
            <nav key={i} className={'aw-strip' + s.cls}>
              {renderPills(s.items)}
            </nav>
          ))}
          <div className="aw-quickmenu-hidebar">
            <button type="button" onClick={hidePills}>
              Hide this menu &times;
            </button>
          </div>
        </div>
      )}

      <div
        className={'aw-drawer' + (open ? ' open' : '')}
        aria-hidden={!open}
        onClick={(e) => {
          if (e.target === e.currentTarget) setOpen(false);
        }}
      >
        <div className="aw-drawer-panel" role="dialog" aria-modal="true" aria-label="Site menu">
          <div className="aw-drawer-close-row">
            <button
              ref={closeBtnRef}
              type="button"
              className="aw-drawer-close"
              aria-label="Close menu"
              onClick={() => setOpen(false)}
            >
              &times;
            </button>
          </div>
          <div className="aw-drawer-settings-row">
            <button
              type="button"
              className="aw-drawer-settings aw-drawer-zoom"
              onClick={zoomOut}
              aria-label="Make the page smaller"
            >
              <ZoomOutIcon />
            </button>
            <button
              type="button"
              className="aw-drawer-settings aw-drawer-zoom"
              onClick={zoomIn}
              aria-label="Make the page bigger"
            >
              <ZoomInIcon />
            </button>
            <button
              type="button"
              className="aw-drawer-settings"
              onClick={() => {
                setOpen(false);
                openSettings();
              }}
            >
              <span aria-hidden="true">&#9881;</span> Settings
            </button>
          </div>
          {sections.map((s) => (
            <div key={s.label}>
              <div className="aw-drawer-sect">{s.label}</div>
              {renderDrawerLinks(s.items)}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default MobileMenu;
